import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import Input from "../../Shared/Input";
import TextArea from "../../Shared/TextArea";
import AvatarUpload from "../../Shared/AvatarUpload";
import ImageUpload from "../../Shared/ImageUpload";
import InfoTooltip from "../../Shared/InfoTooltip";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import AlertModal from "../../Shared/AlertModal";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import { useBookingPage, useUpdateBrandingTab } from "../../../hooks/useBookingPage";
import { validateImageFile } from "../../../utility/imageUploadValidation";
import {
  buildApiErrorMessage,
  createTrimHandler,
} from "../../../utility/formValidationUtils";

/**
 * BusinessIdentityForm — the "Business Identity" section of the booking page:
 * banner image, logo, business name, and business tagline. These are the
 * business-facing fields that appear at the top of the public hero. The
 * tagline is a short, business-framed caption shown under the business name —
 * distinct from the personal "About Me" bio managed by PersonalIdentityForm.
 *
 * All fields persist to PATCH /booking-page/branding via useUpdateBrandingTab.
 */
const BusinessIdentityForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // File state
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  // Removal confirmation dialogs
  const [showLogoRemoveConfirm, setShowLogoRemoveConfirm] = useState(false);
  const [showBannerRemoveConfirm, setShowBannerRemoveConfirm] = useState(false);

  // Track the server's current image URLs so a cache refresh that returns
  // different images clears any stale pending file selection (mirrors the
  // profile-photo guard in PersonalIdentityForm).
  const originalLogoUrlRef = useRef(null);
  const originalBannerUrlRef = useRef(null);

  // Character count for the business tagline
  const maxTaglineCharacters = 500;
  const [taglineCharCount, setTaglineCharCount] = useState(0);

  const { data: configData, isLoading } = useBookingPage();

  const { mutate: updateBranding, isPending: isUpdating } =
    useUpdateBrandingTab();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty, errors: formErrors },
  } = useForm({
    defaultValues: {
      bannerImageUrl: "",
      logoUrl: "",
      businessName: "",
      businessTagline: "",
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (configData?.data) {
      const incomingLogo = configData.data.logoUrl || null;
      const incomingBanner = configData.data.bannerImageUrl || null;

      // Server returned a different image URL — clear any pending file
      // selection so we don't re-upload a stale file after a save.
      if (incomingLogo !== originalLogoUrlRef.current) {
        setLogoFile(null);
        originalLogoUrlRef.current = incomingLogo;
      }
      if (incomingBanner !== originalBannerUrlRef.current) {
        setBannerFile(null);
        originalBannerUrlRef.current = incomingBanner;
      }

      reset(
        {
          bannerImageUrl: configData.data.bannerImageUrl || "",
          logoUrl: configData.data.logoUrl || "",
          businessName: configData.data.businessName || "",
          businessTagline: configData.data.businessTagline || "",
        },
        { keepDirty: false }
      );

      setTaglineCharCount(configData.data.businessTagline?.length || 0);
    }
  }, [configData, reset]);

  // Blob URL cleanup is handled by the useFileUpload hook inside AvatarUpload
  // and ImageUpload, so no cleanup is needed here.

  const performBrandingSave = (data) => {
    return new Promise((resolve) => {
      updateBranding(data, {
        onSuccess: () => {
          setLogoFile(null);
          setBannerFile(null);
          resolve({ ok: true });
        },
        onError: (err) => {
          resolve({ ok: false, err });
        },
      });
    });
  };

  const flashSuccess = (message) => {
    setSuccessMessage(message);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  // --- Removal handlers with auto-save ---

  // Removes an image field by saving the "null" sentinel, then surfaces the
  // outcome. On failure the field value is restored so the UI doesn't claim
  // the image is gone while the server still has it.
  const handleImageRemove = async ({
    fieldName,
    previousUrl,
    clearFile,
    closeConfirm,
    successText,
    failureFallback,
  }) => {
    setValue(fieldName, null, { shouldDirty: true });
    clearFile(null);
    setErrors({});
    setSuccess(false);
    closeConfirm(false);

    const formData = new FormData();
    formData.append(fieldName, "null");
    const result = await performBrandingSave(formData);

    if (!result.ok) {
      // Restore the previous value — the removal did not take effect.
      setValue(fieldName, previousUrl, { shouldDirty: false });
      setErrors({
        title: "Error",
        message: buildApiErrorMessage(result.err, failureFallback),
      });
      return;
    }

    flashSuccess(successText);
  };

  const handleConfirmedLogoRemove = () =>
    handleImageRemove({
      fieldName: "logoUrl",
      previousUrl: originalLogoUrlRef.current,
      clearFile: setLogoFile,
      closeConfirm: setShowLogoRemoveConfirm,
      successText: "Logo removed successfully",
      failureFallback: "Failed to remove logo",
    });

  const handleConfirmedBannerRemove = () =>
    handleImageRemove({
      fieldName: "bannerImageUrl",
      previousUrl: originalBannerUrlRef.current,
      clearFile: setBannerFile,
      closeConfirm: setShowBannerRemoveConfirm,
      successText: "Banner image removed successfully",
      failureFallback: "Failed to remove banner image",
    });

  // --- Main submit ---

  const onSubmit = async (data) => {
    setErrors({});
    setSuccess(false);

    // Validate files
    for (const [file, label] of [
      [logoFile, "Logo"],
      [bannerFile, "Banner"],
    ]) {
      if (file) {
        try {
          validateImageFile(file);
        } catch (error) {
          setErrors({
            title: "File Validation Error",
            message: `${label}: ${error.message}`,
          });
          return;
        }
      }
    }

    const fileUploads = [
      logoFile && "logo",
      bannerFile && "banner",
    ].filter(Boolean);

    const brandingFormData = new FormData();
    brandingFormData.append("businessName", data.businessName ?? "");
    brandingFormData.append("businessTagline", data.businessTagline ?? "");
    if (logoFile) brandingFormData.append("logo", logoFile);
    if (bannerFile) brandingFormData.append("bannerImage", bannerFile);

    const result = await performBrandingSave(brandingFormData);
    if (!result.ok) {
      setErrors({
        title: "Error",
        message: buildApiErrorMessage(
          result.err,
          "Failed to update business identity"
        ),
      });
      return;
    }

    const uploadLabels =
      fileUploads.length > 0 ? ` and ${fileUploads.join(", ")} uploaded` : "";
    flashSuccess(`Business identity updated successfully${uploadLabels}`);
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showFileUpload={true} inputCount={1} />
      </TabFormWrapper>
    );
  }

  return (
    <>
      {/* Logo removal confirmation */}
      <AlertModal
        isOpen={showLogoRemoveConfirm}
        modalTitle="Remove Logo"
        modalMessage="Are you sure you want to remove your business logo? This action cannot be undone."
        primaryCaption="Remove Logo"
        primaryButtonColour="red"
        secondaryCaption="Cancel"
        primaryClickHandler={handleConfirmedLogoRemove}
        dismissModalHandler={() => setShowLogoRemoveConfirm(false)}
        showIcon={true}
      />

      {/* Banner removal confirmation */}
      <AlertModal
        isOpen={showBannerRemoveConfirm}
        modalTitle="Remove Banner Image"
        modalMessage="Are you sure you want to remove your banner image? This action cannot be undone."
        primaryCaption="Remove Banner"
        primaryButtonColour="red"
        secondaryCaption="Cancel"
        primaryClickHandler={handleConfirmedBannerRemove}
        dismissModalHandler={() => setShowBannerRemoveConfirm(false)}
        showIcon={true}
      />

      <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <div className="px-4 py-6 sm:p-8">
          <div className="space-y-6">
            {/* Banner image — first in public hero reading order */}
            <ImageUpload
              label="Banner Image"
              name="bannerImage"
              accept="image/*"
              uniqueId="banner-upload"
              defaulturl={configData?.data?.bannerImageUrl || null}
              filelist={bannerFile ? [bannerFile] : []}
              onRemoveHandler={() => {
                setBannerFile(null);
                setValue("bannerImageUrl", null, { shouldDirty: true });
              }}
              onRemoveConfirmHandler={() => setShowBannerRemoveConfirm(true)}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setBannerFile(e.target.files[0]);
                  setValue("bannerImageUrl", e.target.files[0].name, {
                    shouldDirty: true,
                  });
                }
              }}
              description={
                <span className="inline-flex items-center gap-1.5">
                  A wide, landscape photo works best.
                  <InfoTooltip
                    label="Banner image size"
                    content="Aim for around 1600×600px. Your image spans the full width — very tall or square images get trimmed at the top and bottom."
                  />
                </span>
              }
            />

            {/* Logo — second in public hero reading order */}
            <AvatarUpload
              label="Logo"
              name="logo"
              accept="image/*"
              uniqueId="logo-upload"
              defaulturl={configData?.data?.logoUrl || null}
              filelist={logoFile ? [logoFile] : []}
              onRemoveHandler={() => {
                setLogoFile(null);
                setValue("logoUrl", null, { shouldDirty: true });
              }}
              onRemoveConfirmHandler={() => setShowLogoRemoveConfirm(true)}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setLogoFile(e.target.files[0]);
                  setValue("logoUrl", e.target.files[0].name, {
                    shouldDirty: true,
                  });
                }
              }}
              description="Upload your business logo. Recommended size: 200x200px."
            />

            {/* Business name */}
            <Input
              label="Business Name"
              name="businessName"
              placeholder="Enter your business name"
              errormessage={errors?.businessName?.message || formErrors?.businessName?.message}
              {...register("businessName", {
                maxLength: {
                  value: 100,
                  message: "Business name cannot exceed 100 characters",
                },
                onBlur: createTrimHandler(setValue, "businessName"),
              })}
            />

            {/* Business tagline */}
            <TextArea
              label="Business Tagline"
              name="businessTagline"
              rows={4}
              placeholder="A short line about your business (e.g. Serving downtown since 2015)"
              helperText="Shown under your business name on the public booking page. This is separate from your personal About Me bio."
              charCount={taglineCharCount}
              maxLength={maxTaglineCharacters}
              errormessage={errors?.businessTagline?.message || formErrors?.businessTagline?.message}
              {...register("businessTagline", {
                maxLength: {
                  value: maxTaglineCharacters,
                  message: `Business tagline cannot exceed ${maxTaglineCharacters} characters`,
                },
                onBlur: (e) => {
                  const trimmed = e.target.value.trim();
                  setValue("businessTagline", trimmed, {
                    shouldValidate: true,
                  });
                  // Keep the counter in sync with the trimmed value that will
                  // actually be submitted.
                  setTaglineCharCount(trimmed.length);
                },
                onChange: (e) => {
                  setTaglineCharCount(e.target.value.length);
                  setValue("businessTagline", e.target.value, {
                    shouldValidate: true,
                  });
                },
              })}
            />
          </div>
        </div>

        <FormFooter
          primaryCaption="Save"
          isLoading={isUpdating}
          disabled={!isDirty && !logoFile && !bannerFile}
          requiresVerification={false}
        />

        {errors.title && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertRed title={errors.title} message={errors.message} />
          </div>
        )}

        {success && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertGreen title="Success" message={successMessage} />
          </div>
        )}
      </TabFormWrapper>
    </>
  );
};

export default BusinessIdentityForm;
